import { t as __t } from "i18next";
import React, { useEffect, useRef } from "react";
import { openDocumentPreview } from "../document-preview";
import { dateToString, displayUrl } from "../../utils/commonUtil";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import SwalAlert from "../common/alert";
const LIMIT_FILES = 20;

export default function FileUploadGrid({
    multiple = true,
    filesFromApi = [],
    onChange,
    onDelete,
    folderName = "",
    referenceName = dateToString(new Date(), "yyyyMMDD"),
    accept = "*/*",
    valueFile = null,
    required = false,
    requiredMessage = __t("uiText.text018"),
}) {
    const [files, setFiles] = React.useState([]);
    const [preview, setPreview] = React.useState(null); // { src, name, kind: 'image'|'pdf' }
    const [requiredError, setRequiredError] = React.useState(false);
    const fileInput = useRef();
    const rootRef = useRef();
    const blobUrlsRef = useRef([]);
    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        sidebarTabs: (defaultTabs) => [
            defaultTabs[0], // Thumbnails
            defaultTabs[1], // Bookmarks
            defaultTabs[2], // Attachments
        ],
    });
    useEffect(() => {
        setFiles(Array.isArray(valueFile) ? valueFile : []);
    }, [valueFile]);

    const hasAnyFiles = files.length > 0 || (filesFromApi?.length || 0) > 0;

    useEffect(() => {
        if (hasAnyFiles && requiredError) {
            setRequiredError(false);
        }
    }, [hasAnyFiles, requiredError]);

    // Centralized required validation for every form that reuses FileUploadGrid.
    // The first invalid required FileUploadGrid in the form blocks submit and shows one alert.
    useEffect(() => {
        if (!required) return undefined;

        const root = rootRef.current;
        const form = root?.closest?.("form");
        if (!root || !form) return undefined;

        const handleFormSubmitCapture = (event) => {
            const requiredUploaders = Array.from(
                form.querySelectorAll('[data-file-upload-required="true"]')
            );
            const firstInvalid = requiredUploaders.find(
                (node) => node.getAttribute("data-has-files") !== "true"
            );

            if (firstInvalid !== root) return;

            event.preventDefault();
            event.stopPropagation();
            setRequiredError(true);

            root.scrollIntoView?.({ behavior: "smooth", block: "center" });
            SwalAlert.error(__t("uiText.text019"), requiredMessage);
        };

        form.addEventListener("submit", handleFormSubmitCapture, true);
        return () => form.removeEventListener("submit", handleFormSubmitCapture, true);
    }, [required, requiredMessage]);

    const buildChangePayload = (nextFiles) => ({
        files: nextFiles,
        folderName,
        referenceName,
        hasFiles: nextFiles.length > 0 || (filesFromApi?.length || 0) > 0,
        isEmpty: nextFiles.length === 0 && (filesFromApi?.length || 0) === 0,
    });

    const emitChange = (nextFiles) => {
        setFiles(nextFiles);
        const payload = buildChangePayload(nextFiles);
        onChange?.(payload);

        if (required) {
            setRequiredError(payload.isEmpty);
        }
    };

    const isImageName = (name = "") => /\.(jpe?g|png|gif|webp|bmp)$/i.test(name);
    const isPdfName = (name = "") => /\.pdf$/i.test(name);

    // เลือกไฟล์ใหม่
    const handleChange = (e) => {
        let selected = Array.from(e.target.files || []);
        const available = LIMIT_FILES - files.length - (filesFromApi?.length || 0);
        if (selected.length > available) selected = selected.slice(0, available);

        const newFiles = [...files, ...selected];
        emitChange(newFiles);

        // reset input เพื่อเลือกไฟล์ชื่อเดิมซ้ำได้
        e.target.value = "";
    };

    // ลบไฟล์
    const handleRemove = (idx, type = "local") => {
        if (type === "local") {
            const newFiles = [...files];
            newFiles.splice(idx, 1);
            emitChange(newFiles);
        } else {
            const newApiFiles = [...(filesFromApi || [])];
            const remainingApiCount = Math.max(0, newApiFiles.length - 1);
            onDelete?.({ file: newApiFiles.find((_, i) => i === idx), index: idx });

            if (required && files.length === 0 && remainingApiCount === 0) {
                setRequiredError(true);
            }
        }
    };

    const handlePreview = (payload) => setPreview(payload);
    const handleClosePreview = () => {
        setPreview(null);
        // clear blob urls
        blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
        blobUrlsRef.current = [];
    };

    const allFiles = [
        ...files.map((f) => ({ ...f, _type: "local" })),
        ...(filesFromApi || []).map((f) => ({ ...f, _type: "api" })),
    ];

    const showAddBtn = allFiles.length < LIMIT_FILES && (allFiles.length === 0 || multiple);

    const getApiSrc = (file) => (file?.startsWith?.("http") ? file : displayUrl(file));
    const getApiName = (file) => {
        try {
            const src = getApiSrc(file);
            return decodeURIComponent(src.split("/").pop()?.split("?")[0] || "file");
        } catch {
            return "file";
        }
    };

    const renderPreview = (file, idx, type) => {
        let isImg = false;
        let isPdf = false;
        let src = "";
        let name = "";

        if (type === "api") {
            src = getApiSrc(file);
            name = getApiName(file);
            isImg = isImageName(name) || isImageName(file);
            isPdf = isPdfName(name) || isPdfName(file);
        } else {
            name = file?.name || "file";
            src = URL.createObjectURL(file);
            isImg = file?.type?.startsWith?.("image/") || isImageName(name);
            isPdf = file?.type === "application/pdf" || isPdfName(name);
        }

        const boxStyle = {
            width: 180,
            height: 180,
            border: "1px solid #ddd",
            borderRadius: 8,
            margin: 5,
            position: "relative",
            background: "#fafbfc",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
        };

        return (
            <div key={`${type}-${idx}`} style={boxStyle}>
                {/* Content */}
                {isImg ? (
                    <img
                        src={src}
                        alt={name}
                        style={{ width: 180, height: 180, objectFit: "cover", borderRadius: 6, cursor: "pointer" }}
                        onClick={() => handlePreview({ src, name, kind: "image" })}
                    />
                ) : isPdf ? (
                    <div
                        onClick={() => previewPdfAsBlob(src, name)}
                        style={{
                            width: "100%",
                            height: "100%",
                            cursor: "pointer",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            padding: 10,
                        }}
                        title={__t("uiText.text375")}
                    >
                        <div style={{ fontSize: 46 }}>📄</div>
                        <div style={{ fontSize: 12, textAlign: "center", color: "#333", wordBreak: "break-word" }}>
                            {name}
                        </div>
                        <div style={{ fontSize: 11, color: "#1890ff" }}>{__t("uiText.text014")}</div>
                    </div>
                ) : (
                    <div
                        style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            padding: 10,
                        }}
                    >
                        <div style={{ fontSize: 44 }}>📎</div>
                        <div style={{ fontSize: 12, textAlign: "center", color: "#333", wordBreak: "break-word" }}>
                            {name}
                        </div>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                openDocumentPreview(src, { title: __t("uiText.text003"), name });
                            }}
                            style={{
                                background: "#1890ff",
                                color: "#fff",
                                border: "none",
                                borderRadius: 6,
                                fontSize: 12,
                                padding: "6px 10px",
                                cursor: "pointer",
                            }}
                        >
                            {__t("uiText.text020")}</button>
                    </div>
                )}

                {/* Delete button */}
                {(type === "local" || onDelete) && (
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            handleRemove(idx, type);
                        }}
                        style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            border: "none",
                            background: "#ff4d4f",
                            color: "#fff",
                            borderRadius: "50%",
                            width: 22,
                            height: 22,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            fontSize: 17,
                            lineHeight: 1,
                        }}
                        title={__t("uiText.text021")}
                    >
                        ×
                    </button>
                )}
            </div>
        );
    };

    async function previewPdfAsBlob(pdfUrl, name = "document.pdf") {
        const res = await fetch(pdfUrl, {
            // ຖ້າມີ token ກໍ່ໃສ່ໄດ້
            // headers: { Authorization: `Bearer ${token}` },
            // credentials: "include", // ຖ້າ auth ແບບ cookie
        });

        if (!res.ok) {
            const text = await res.text().catch(() => "");
            throw new Error(`Load PDF failed: ${res.status} ${text}`);
        }

        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        blobUrlsRef.current.push(blobUrl);

        setPreview({ src: blobUrl, name, kind: "pdf" });
    }

    return (
        <div
            ref={rootRef}
            data-file-upload-required={required ? "true" : "false"}
            data-has-files={hasAnyFiles ? "true" : "false"}
        >
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 10,
                    marginBottom: requiredError ? 8 : 15,
                    padding: requiredError ? 8 : 0,
                    border: requiredError ? "1px solid #dc3545" : "1px solid transparent",
                    borderRadius: requiredError ? 8 : 0,
                }}
            >
                {files.map((f, i) => renderPreview(f, i, "local"))}
                {filesFromApi?.map((f, i) => renderPreview(f, i, "api"))}

                {showAddBtn && (
                    <div
                        style={{
                            width: 180,
                            height: 180,
                            border: "2px dashed #aaa",
                            borderRadius: 8,
                            margin: 5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f3f6fa",
                            cursor: "pointer",
                            fontSize: 38,
                            color: "#1890ff",
                        }}
                        onClick={() => fileInput.current.click()}
                        title={__t("uiText.text022")}
                    >
                        +
                    </div>
                )}

                <input
                    ref={fileInput}
                    type="file"
                    multiple={multiple}
                    style={{ display: "none" }}
                    onChange={handleChange}
                    accept={accept}
                />
            </div>

            {requiredError && (
                <div
                    role="alert"
                    style={{
                        marginTop: 4,
                        marginBottom: 12,
                        color: "#dc3545",
                        fontSize: 13,
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                    }}
                >
                    <span aria-hidden="true">⚠</span>
                    <span>{requiredMessage}</span>
                </div>
            )}

            {/* Preview Modal */}
            {preview && (
                <div className="preview-modal">
                    <div className="modal-bg" onClick={handleClosePreview} />

                    <div className="modal-content">
                        {preview.kind === "image" ? (
                            <img
                                src={preview.src}
                                alt={preview.name}
                                style={{
                                    maxWidth: "90vw",
                                    maxHeight: "85vh",
                                    borderRadius: 12,
                                    boxShadow: "0 2px 20px rgba(0,0,0,0.2)",
                                }}
                            />
                        ) : preview.kind === "pdf" ? (
                            <div style={{ width: "90vw", height: "85vh", background: "#fff", borderRadius: 12,  }}>
                                {/* <iframe
                                    title={preview.name}
                                    src={preview.src}
                                    style={{ width: "100%", height: "100%", border: "0" }}
                                /> */}
                                <Worker  workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}>
                                    <Viewer fileUrl={preview.src} plugins={[defaultLayoutPluginInstance]} />
                                </Worker>
                            </div>
                        ) : null}

                        <button
                            onClick={handleClosePreview}
                            style={{
                                position: "absolute",
                                right: 18,
                                top: 10,
                                fontSize: 34,
                                color: "#fff",
                                background: "rgba(0,0,0,0.25)",
                                border: "none",
                                cursor: "pointer",
                                width: 44,
                                height: 44,
                                borderRadius: 999,
                            }}
                            title={__t("uiText.text009")}
                        >
                            ×
                        </button>
                    </div>

                    <style>{`
            .preview-modal {
              position: fixed; z-index: 9999; inset: 0;
              display: flex; align-items: center; justify-content: center;
              animation: fadeIn .22s;
            }
            .modal-bg {
              position: absolute; inset: 0;
              background: rgba(0,0,0,0.65); animation: fadeIn .18s;
            }
            .modal-content {
              position: relative;
              z-index: 1;
              padding: 12px 22px;
              background: transparent;
              border-radius: 12px;
              display: flex;
              align-items: center;
              justify-content: center;
              animation: scaleIn .25s;
            }
            @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
            @keyframes scaleIn { from { transform: scale(0.92); opacity: 0.6 } to { transform: scale(1); opacity: 1 } }
          `}</style>
                </div>
            )}
        </div>
    );
}